import { computed } from 'vue'
import { useQuery, keepPreviousData } from '@tanstack/vue-query'
import { configApi } from '@/api'
import type { FlagRead } from '@/api'
import { POLLING_INTERVALS, STALE_TIME, RETRY_CONFIG } from '@/config/query'

export function useFlags() {
  const { data, error, isFetching, refetch } = useQuery({
    queryKey: ['config', 'flags'],
    queryFn: async () => {
      const response = await configApi.getAllFlags()
      if (response.success && response.data) {
        return response.data
      }
      throw new Error(response.error ?? 'Failed to fetch flags')
    },
    refetchInterval: POLLING_INTERVALS.FEATURE_FLAGS,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    placeholderData: keepPreviousData,
    retry: RETRY_CONFIG.ATTEMPTS,
    staleTime: STALE_TIME.MEDIUM,
  })

  const getFlag = (name: string) => {
    return computed(() => data.value?.find((f: FlagRead) => f.title === name))
  }

  return {
    flags: computed(() => data.value ?? []),
    getFlag,
    isLoading: computed(() => isFetching.value && !data.value),
    hasError: computed(() => Boolean(error.value)),
    errorMessage: computed(() => error.value?.message ?? 'Failed to load flags'),
    refetch,
  }
}
